import { inject, Injectable, Signal } from '@angular/core';
import { AbsenceReportDTO } from '@app/shared/services/dtos/reports/absence-report.dto';
import { Company } from '@app/shared/types/models/company/company.model';
import { AbsenceReport } from '@app/shared/types/models/reports/absence-report';
import { CompaniesDataService } from '../../companies-data.service';
import { UsersDataService } from '../../users-data.service';
import { AbstractFactory } from '../abstract.factory';
import { ReportMappers } from './report-mappers';
import { WorkTimeRegistrationFactory } from './work-time-registration.factory';

/**
 * AbsenceReportFactory - Maps Absence report DTOs from GET /time-reports into domain models.
 *
 * Resolves `company` from nested `company` (upsert into {@link CompaniesDataService})
 * when provided. Upserts nested `user` into {@link UsersDataService}.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used by {@link TimeReportFactory} when `type` is `AbsenceReport`.
 */
@Injectable({ providedIn: 'root' })
export class AbsenceReportFactory extends AbstractFactory<AbsenceReportDTO, AbsenceReport> {
  private readonly usersService = inject(UsersDataService);
  private readonly companiesService = inject(CompaniesDataService);
  private readonly workTimeRegistrationFactory = inject(WorkTimeRegistrationFactory);

  public produce(item: AbsenceReportDTO): AbsenceReport {
    if (!item) return undefined;

    if (item.user) {
      this.usersService.upsertLocalData(item.user);
    }
    if (item.createdBy) {
      this.usersService.upsertLocalData(item.createdBy);
    }
    if (item.company) {
      this.companiesService.upsertLocalData(item.company);
    }

    const userId = item.user?.id;
    const createdById = item.createdBy?.id;

    return new AbsenceReport({
      id: item.id,
      description: item.description,
      status: ReportMappers.mapStatus(item.status),
      date: item.date ? new Date(item.date) : undefined,
      duration: item.duration,
      dateFrom: item.dateFrom ? new Date(item.dateFrom) : undefined,
      dateTo: item.dateTo ? new Date(item.dateTo) : undefined,
      user: userId ? this.usersService.getById(userId) : undefined,
      createdBy: createdById ? this.usersService.getById(createdById) : undefined,
      reportType: 'Absence',
      createdOn: item.createdOn ? new Date(item.createdOn) : undefined,
      comments: item.comments,
      registration: item.registration ? this.workTimeRegistrationFactory.produce(item.registration) : undefined,
      company: this.resolveCompany(item),
      absenceType: ReportMappers.mapAbsenceType(item.absenceType)
    });
  }

  private resolveCompany(item: AbsenceReportDTO): Signal<Company | undefined> | undefined {
    if (item.company?.id) {
      this.companiesService.upsertLocalData(item.company);
      return this.companiesService.getById(item.company.id);
    }

    return undefined;
  }
}
